﻿import React from 'react'
import moment from 'moment'
import { Autocomplete, TextField as MuiTextField, Tooltip } from '@mui/material'
import { createStyles, withStyles } from '@mui/styles'
import { defaultStyles, muiOptions } from '../../../infrastructure/materialUiThemeProvider'
import { t } from '../../../infrastructure/i18nextHelper'
import { Guid } from '../../../infrastructure/guid'
import { Select, DatePicker, TextField, Switch, DateRangePicker } from '../../common/customComponents'
import {
    activateVesselReferenceIdMasterDataFeature, CertificateOfConformity, VesselMeanOfTransportation,
    getMatchingVesselReferenceVehicles, vesselMeanOfTransportations, VesselReferenceVehicle, VesselStatus
} from '../vesselModels'
import { VesselEditContainer } from './vesselEditStore'
import { hasFeature } from '../../../infrastructure/feature'
import { UserContextContainer } from '../../../infrastructure/signIn/userContext'
import { CommentContent } from '../../common/comment/commentContent'
import { CommentButton } from '../../common/comment/commentButton'
import { Item } from '../../common/components/select/types'
import { SapButton } from '../../../infrastructure/sap/components/sapButton'

function _FirstLine({ classes }) {
    let vessel = VesselEditContainer.useContainer()
    let user = UserContextContainer.useContainer()

    let hasDealAssigned = vessel.state.products?.some(p => p.purchaseMovements?.some(pm => pm.sites?.some(s => s.dealId)))

    if (!vessel.state) return <div />
    let state = vessel.state
    let referenceIdMasterDataActive = hasFeature(activateVesselReferenceIdMasterDataFeature)
    let matchingVehicles = getMatchingVesselReferenceVehicles(
        vessel.referenceVehicles, user.currentCountry, state.meanOfTransportation)
    let selectedVehicle = state.referenceID
        ? vessel.referenceVehicles.find(x => x.vehicleId === state.referenceID)
        : null
    let referenceVehicleOptions = matchingVehicles
    if (selectedVehicle) {
        let selectedVehicleId = selectedVehicle.vehicleId
        if (!referenceVehicleOptions.some(x => x.vehicleId === selectedVehicleId))
            referenceVehicleOptions = [selectedVehicle, ...referenceVehicleOptions]
    }

    return (
        <div className={classes.row}>
            <TextField label={t('vessels.label.name')}
                status={vessel.formErrors.vesselFieldStatus.getStatus('name')}
                onChange={event => {
                    vessel.setState({ ...state, name: event.target.value })
                    vessel.formErrors.vesselFieldStatus.setStatus('name', 'none')
                }}
                text={state.name} />
            <Select label={t('vessels.label.jetty')}
                value={state.jettyCode ?? ''}
                status={vessel.formErrors.vesselFieldStatus.getStatus('jettyCode')}
                choices={vessel.jettys.map(x => x.code)}
                onChange={val => vessel.changeJetty(val)} />
            <Select label={t('vessels.label.counterparty')}
                value={state.counterpartySupplierId}
                status={vessel.formErrors.vesselFieldStatus.getStatus('counterpartySupplierId')}
                choices={vessel.counterpartys.map(x => ({ value: x.id, text: x.name }))}
                onChange={vessel.changeCounterparty}
                isClearable={true}
                clearableLabel={t('vessels.label.counterparty')} />
            <Select label={t('vessels.label.assignee')}
                value={state.assigneeUserName}
                isClearable clearableLabel=' '
                choices={vessel.vesselUsers.map(x => ({ value: x.login, text: x.fullName }))}
                onChange={val => vessel.setState({ ...state, assigneeUserName: val })} />
            <Select label={t('vessels.label.meanOfTransportation')}
                value={state.meanOfTransportation}
                isClearable={false}
                status={vessel.formErrors.vesselFieldStatus.getStatus('meanOfTransportation')}
                choices={vesselMeanOfTransportations}
                onChange={val => vessel.changeMeanOfTransportation(val as VesselMeanOfTransportation)} />
            <Tooltip title={t('vessels.label.referenceIdTooltip')} placement='top'>
                <span style={{ display: 'inline-flex' }}>
                    {referenceIdMasterDataActive
                        ? <Autocomplete<VesselReferenceVehicle>
                            sx={{ width: 220 }}
                            options={referenceVehicleOptions}
                            value={selectedVehicle}
                            getOptionLabel={vehicle => `${vehicle.vehicleId} - ${vehicle.vehicleName}`}
                            isOptionEqualToValue={(option, value) => option.vehicleId === value.vehicleId}
                            getOptionDisabled={option => !matchingVehicles.some(x => x.vehicleId === option.vehicleId)}
                            onChange={(_, vehicle) => {
                                vessel.setState({
                                    ...state,
                                    referenceID: vehicle?.vehicleId ?? null,
                                    ...(vehicle ? { name: vehicle.vehicleName } : {})
                                })
                                vessel.formErrors.vesselFieldStatus.setStatus('referenceID', 'none')
                            }}
                            renderInput={params =>
                                <MuiTextField {...params}
                                    label={t('vessels.label.referenceId')}
                                    variant='filled'
                                    error={vessel.formErrors.vesselFieldStatus.getStatus('referenceID') === 'alert'}
                                    helperText={vessel.formErrors.vesselFieldStatus.getStatus('referenceID') === 'alert'
                                        ? t('components.error')
                                        : undefined}
                                />
                            }
                        />
                        : <TextField label={t('vessels.label.referenceId')}
                            onChange={event => vessel.setState({ ...state, referenceID: event.target.value })}
                            text={state.referenceID} />
                    }
                </span>
            </Tooltip>
        </div>
    )
}

function _SecondLine({ classes }) {
    let vessel = VesselEditContainer.useContainer()

    if (!vessel.state) return <div />
    let state = vessel.state

    return (
        <div className={classes.row}>

            <DateRangePicker startDate={state.originalLaycanStartDate} startLabel={t('vessels.label.originalLaycanStart')}
                endDate={state.originalLaycanEndDate} endLabel={t('vessels.label.originalLaycanEnd')}
                startStatus={vessel.formErrors.vesselFieldStatus.getStatus('originalLaycanStartDate')}
                endStatus={vessel.formErrors.vesselFieldStatus.getStatus('originalLaycanEndDate')} autoReplace={true} withoutAutofill
                onPeriodChange={period => vessel.setState(
                    { ...state, originalLaycanStartDate: period.startDate, originalLaycanEndDate: period.endDate })} />
            <DateRangePicker startDate={state.revisedLaycanStartDate} startLabel={t('vessels.label.revisedLaycanStart')}
                endDate={state.revisedLaycanEndDate} endLabel={t('vessels.label.revisedLaycanEnd')} autoReplace={true}
                startAuto={!state.revisedLaycanStartDateOverwritten} endAuto={!state.revisedLaycanEndDateOverwritten}
                onPeriodChange={period => {
                    vessel.setState({
                        ...state,
                        revisedLaycanStartDate: period.startDate,
                        revisedLaycanStartDateOverwritten: !!period.startDate && moment(period.startDate).isValid(),
                        revisedLaycanEndDate: period.endDate,
                        revisedLaycanEndDateOverwritten: !!period.endDate && moment(period.endDate).isValid()
                    })
                }} />
            {hasFeature('SapPoFromVessel') && !hasFeature('SapPurchaseContractCreate') &&
                <>
                    <SapButton
                        label={t('vessels.label.createSapPurchaseOrder')}
                        tooltip={t('vessels.label.sapButtonTooltip')}
                        disabled={!vessel.canCreatePurchaseOrder || !!vessel.state.sapPurchaseOrder}
                        onClick={vessel.createPurchaseOrder}
                    />
                    <TextField label={t('vessels.label.sapPurchaseOrder')}
                        text={state.sapPurchaseOrder}
                        disabled={!vessel.canEditPurchaseOrder}
                        onChange={e => vessel.setState({ ...state, sapPurchaseOrder: e.target.value, isPurchaseOrderFromSap: false })}
                    />
                </>
            }
        </div>
    )
}

function _ThirdLine({ classes }) {
    let vessel = VesselEditContainer.useContainer()

    if (!vessel.state) return <div />
    let state = vessel.state

    return (
        <div className={classes.row}>
            {vessel.isAuthorized('norDate') &&
                <DatePicker date={state.norDate}
                    auto={!state.norDateOverwritten}
                    label={t('vessels.label.norDate')}
                    setDate={newDate => vessel.setState({ ...state, norDate: newDate, norDateOverwritten: !!newDate && moment(newDate).isValid() })} />}
            {vessel.isAuthorized('etaDate') &&
                <DatePicker date={state.etaDate}
                    auto={!state.etaDateOverwritten}
                    label={t('vessels.label.etaDate')}
                    setDate={newDate => vessel.setState({ ...state, etaDate: newDate, etaDateOverwritten: !!newDate && moment(newDate).isValid() })} />}
            <DatePicker date={state.berthingDate}
                auto={!state.berthingDateOverwritten}
                label={t('vessels.label.berthingDate')}
                setDate={newDate => vessel.setState({ ...state, berthingDate: newDate, berthingDateOverwritten: !!newDate && moment(newDate).isValid() })} />
            <DatePicker date={state.completionOfDischargeDate}
                auto={!state.completionOfDischargeDateOverwritten}
                label={t('vessels.label.cod')}
                status={vessel.formErrors.vesselFieldStatus.getStatus('completionOfDischargeDate')}
                setDate={newDate => {
                    vessel.setState({ ...state, completionOfDischargeDate: newDate, completionOfDischargeDateOverwritten: !!newDate && moment(newDate).isValid() })
                    vessel.formErrors.vesselFieldStatus.setStatus('completionOfDischargeDate', 'none')
                }} />
            <DatePicker
                date={state.availabilityDate}
                auto={!state.availabilityDateOverwritten}
                label={t('vessels.label.availabilityDate')}
                setDate={newDate => vessel.setState({ ...state, availabilityDate: newDate, availabilityDateOverwritten: !!newDate && moment(newDate).isValid() })} />
            {vessel.isAuthorized('bankGuaranteeDate') &&
                <DatePicker
                    date={state.bankGuaranteeDate}
                    auto={!state.bankGuaranteeDateOverwritten}
                    label={t('vessels.label.bankGuaranteeDate')}
                    setDate={newDate => vessel.setState({ ...state, bankGuaranteeDate: newDate, bankGuaranteeDateOverwritten: !!newDate && moment(newDate).isValid() })} />}
        </div>
    )
}

function _FourthLine({ classes }) {
    let vessel = VesselEditContainer.useContainer()

    let certificateOfConformitys: Item[] =
        Object.keys(CertificateOfConformity).filter(x => isNaN(+x)).map((x, i) => { return { value: i.toString(), text: t('vessels.types.certificateOfConformity.' + x.valueOf()) } })

    let updatePaymentTermsDate = (newDate: string | null) => {
        if (!newDate)
            vessel.setState({ ...state, paymentTermsDate: null, paymentTermsDateOverwritten: false })
        else
            vessel.setState({ ...state, paymentTermsDate: newDate, paymentTermsDateOverwritten: true })
    }

    if (!vessel.state) return <div />
    let state = vessel.state

    return (
        <div className={classes.row}>
            {vessel.isAuthorized('certificateOfConformity') &&
                <Select label={t('vessels.label.certificate')}
                    value={state.certificateOfConformity}
                    choices={certificateOfConformitys}
                    onChange={val => vessel.setState({ ...state, certificateOfConformity: +val })} />}
            {vessel.isAuthorized('vesselClearance') &&
                <Switch form label={t('vessels.label.clearance')}
                    isChecked={state.hasClearance}
                    changeCallback={() => vessel.setState({ ...state, hasClearance: !state.hasClearance })}
                    offText={'No'} onText={'Yes'} />}
            <Select label={t('vessels.label.paymentTerms')}
                isClearable
                clearableLabel={' '}    
                value={state.paymentTerms !== null ? state.paymentTerms : ''}
                choices={vessel.paymentTerms.map(x => ({ value: x.value.toString(), text: t('referential.paymentTerms.' + x.code) }))}
                onChange={val => { vessel.setState({ ...state, paymentTerms: val ? +val : null }) }} />
            <DatePicker date={state.paymentTermsDate} label={t('vessels.label.paymentTermsDate')} auto={!state.paymentTermsDateOverwritten}
                setDate={newDate => updatePaymentTermsDate(newDate)} />
            {vessel.isAuthorized('vettingNumber') &&
                <TextField label={t('vessels.label.vettingNumber')}
                    onChange={event => vessel.setState({ ...state, vettingNumber: event.target.value })}
                    text={state.vettingNumber} />}
        </div>
    )
}

function _FifthLine({ classes }) {
    let vessel = VesselEditContainer.useContainer()
    if (!vessel.state) return <div />

    let associationKey = `vessel-${vessel.state.id}`

    return (
        <div className={classes.row}>
            <CommentContent associationKey={associationKey} />
            <CommentButton withPadding associationKey={associationKey} />
        </div>
    )
}

let styles = _ =>
    createStyles({
        row: {
            ...defaultStyles.flexRow,
            justifyContent: 'flex-start',
            alignItems: 'center',
            marginTop: '1em',
            marginBottom: '1em'
        }
    })

export let FirstLine = withStyles(styles, muiOptions)(_FirstLine)
export let SecondLine = withStyles(styles, muiOptions)(_SecondLine)
export let ThirdLine = withStyles(styles, muiOptions)(_ThirdLine)
export let FourthLine = withStyles(styles, muiOptions)(_FourthLine)
export let FifthLine = withStyles(styles, muiOptions)(_FifthLine)
